/**
 * Connectors Page
 * Dynamic connector management — types fetched from /connectors/types API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Plug, RefreshCw, Loader2 } from 'lucide-react';
import ConnectorSetupModal from '../components/dashboard/ConnectorSetupModal';
import SyncStatusCard from '../components/dashboard/SyncStatusCard';
import ConnectorSettingsDrawer from '../components/dashboard/ConnectorSettingsDrawer';
import AudioUploadModal from '../components/dashboard/AudioUploadModal';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import {
  getConnectorTypes,
  getConnectors,
  createConnector,
  activateConnector,
  pauseConnector,
  triggerSync,
} from '../services/connectorService';

const ConnectorsPage = () => {
  const navigate = useNavigate();
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [preselectedType, setPreselectedType] = useState(null);
  const [syncingIds, setSyncingIds] = useState(new Set());
  const [togglingId, setTogglingId] = useState(null);
  const [settingsConnectorId, setSettingsConnectorId] = useState(null);
  const [uploadConnector, setUploadConnector] = useState(null);
  const busyRef = useRef(false);
  const settingsConnector = connectors.find(c => c.id === settingsConnectorId) || null;
  const unconfiguredTypes = connectorTypes.filter(
    type => !connectors.some(c => c.connector_type === type.connector_type)
  );

  // Full initial load (with spinner)
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [typesRes, connectorsRes] = await Promise.all([
      getConnectorTypes(),
      getConnectors(),
    ]);

    if (typesRes.success) {
      setConnectorTypes(typesRes.data);
    } else {
      toast.error(typesRes.error || 'Failed to load connector types');
    }

    if (connectorsRes.success) {
      setConnectors(connectorsRes.data?.items || []);
    } else {
      toast.error(connectorsRes.error || 'Failed to load connectors');
    }

    setLoading(false);
  }, []);

  // Silent refresh (no spinner, skipped when an action is in-flight)
  const refreshConnectors = useCallback(async () => {
    if (busyRef.current) return;
    const res = await getConnectors();
    if (res.success) setConnectors(res.data?.items || []);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds (silent)
  useEffect(() => {
    const interval = setInterval(refreshConnectors, 30000);
    return () => clearInterval(interval);
  }, [refreshConnectors]);

  const handleCreate = async (formData) => {
    busyRef.current = true;
    const result = await createConnector(formData);
    busyRef.current = false;
    if (result.success) {
      toast.success('Connector created successfully');
      setShowModal(false);
      refreshConnectors();
    } else {
      toast.error(result.error);
    }
  };

  const handleSync = async (connectorId) => {
    busyRef.current = true;
    setSyncingIds(prev => new Set(prev).add(connectorId));
    const result = await triggerSync(connectorId);
    setSyncingIds(prev => { const s = new Set(prev); s.delete(connectorId); return s; });
    busyRef.current = false;
    if (result.success) {
      const sr = result.data;
      toast.success(`Sync complete: ${sr.records_stored} records stored`);
      refreshConnectors();
    } else {
      const conn = connectors.find(c => c.id === connectorId);
      const msg = result.error?.replace(/Connector \d+/gi, conn?.display_name || 'This connector');
      toast.error(msg || 'Sync failed');
    }
  };

  const handleActivate = async (connectorId) => {
    busyRef.current = true;
    setTogglingId(connectorId);
    const result = await activateConnector(connectorId);
    setTogglingId(null);
    busyRef.current = false;
    if (result.success) {
      toast.success('Connector activated');
      setConnectors(prev => prev.map(c =>
        c.id === connectorId ? { ...c, status: 'active', error_message: null } : c
      ));
      refreshConnectors();
    } else {
      toast.error(result.error);
    }
  };

  const handlePause = async (connectorId) => {
    busyRef.current = true;
    setTogglingId(connectorId);
    const result = await pauseConnector(connectorId);
    setTogglingId(null);
    busyRef.current = false;
    if (result.success) {
      toast.success('Connector paused');
      setConnectors(prev => prev.map(c =>
        c.id === connectorId ? { ...c, status: 'inactive' } : c
      ));
      refreshConnectors();
    } else {
      toast.error(result.error);
    }
  };

  const openModalForType = (connectorType) => {
    setPreselectedType(connectorType);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setPreselectedType(null);
  };

  const handleCloseSettings = () => {
    const btn = document.getElementById(`settings-btn-${settingsConnectorId}`);
    setSettingsConnectorId(null);
    requestAnimationFrame(() => btn?.focus());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-base font-semibold text-gray-700">Connectors</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Connect external systems to ingest interaction data into the analytics pipeline.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchData}
            className="flex items-center space-x-1 text-xs text-gray-600 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={12} />
            <span>Refresh</span>
          </button>
          {/* Both entry points open the setup modal, which POSTs to
              connector_service (analytics:connectors, admin-only). */}
          <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
            <button
              onClick={() => openModalForType(null)}
              className="btn-primary text-xs px-3 py-1.5 flex items-center space-x-1"
            >
              <Plus size={12} />
              <span>Add Connector</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Connected Connectors */}
      {connectors.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Configured Connectors ({connectors.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connectors.map(connector => (
              <SyncStatusCard
                key={connector.id}
                connector={connector}
                onSync={handleSync}
                onActivate={handleActivate}
                onPause={handlePause}
                onSettings={(id) => setSettingsConnectorId(id)}
                onViewData={(id) => navigate(`/connectors/${id}/interactions`)}
                onUploadAudio={(c) => setUploadConnector(c)}
                syncing={syncingIds.has(connector.id)}
                toggling={togglingId === connector.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Types (unconfigured) */}
      {connectorTypes.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Available Connectors
          </h3>
          {unconfiguredTypes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unconfiguredTypes.map(type => (
                <div key={type.connector_type} className="rounded-lg border border-gray-200 p-4 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Plug size={16} className="text-gray-400" />
                      <h4 className="text-sm font-medium text-gray-900">{type.display_name}</h4>
                    </div>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      Not connected
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{type.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {type.supported_interaction_types.map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{t}</span>
                      ))}
                    </div>
                    <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
                      <button
                        onClick={() => openModalForType(type.connector_type)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Connect
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 rounded-lg border border-dashed border-gray-200 bg-gray-50">
              <Plug size={28} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">More integrations coming soon</p>
              <p className="text-xs text-gray-300 mt-1">We're working on new connectors to expand your analytics pipeline.</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {connectors.length === 0 && connectorTypes.length === 0 && (
        <div className="text-center py-16">
          <Plug size={40} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-sm font-medium text-gray-700 mb-1">No connectors available</h3>
          <p className="text-xs text-gray-500">Connector types will appear once the service is running.</p>
        </div>
      )}

      {/* Setup Modal */}
      <ConnectorSetupModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleCreate}
        connectorTypes={connectorTypes}
        existingConnectors={connectors}
        preselectedType={preselectedType}
      />

      {/* Settings Drawer */}
      <ConnectorSettingsDrawer
        isOpen={!!settingsConnectorId}
        connector={settingsConnector}
        connectorTypes={connectorTypes}
        onClose={handleCloseSettings}
        onConnectorUpdated={refreshConnectors}
      />

      {/* Audio Upload Modal (audio_upload connectors only) */}
      <AudioUploadModal
        isOpen={!!uploadConnector}
        connector={uploadConnector}
        onClose={() => setUploadConnector(null)}
        onSuccess={() => refreshConnectors()}
      />
    </div>
  );
};

export default ConnectorsPage;
